import React, { useState, useEffect } from "react";
import {
  ArrowRight,
  Shield,
  CheckCircle,
  Smartphone,
  Globe,
  CreditCard,
  QrCode,
  Star,
  Users,
  TrendingUp,
  Zap,
  Headphones,
  ChevronDown,
  Menu,
  X,
  Clock,
  Wifi,
  Lock,
  Building2,
  MousePointer,
  ArrowLeftRight,
  Activity,
  BarChart3,
  Bell,
  Video,
  Eye,
  Settings,
  Database,
  Cpu,
  FileText,
  Play,
  Mail,
  BarChart,
  ShoppingCart,
  Brain,
  Phone,
  Target,
  MessageSquare,
  UserCheck,
  DollarSign,
  Coins,
  Banknote,
  ChevronLeft,
  ChevronRight,
  Search
} from "lucide-react";

const HeroSlider = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlay, setIsAutoPlay] = useState(true);

  // Slide data
  const slides = [
    {
      id: 1,
      title: "Seamless Banking",
      subtitle: "Solutions",
      description: "Elevate Your Business with Our Cutting-Edge Banking Services for Secure and Effortless Transactions",
      image: "./banking.png",
      gradient: "from-blue-50 to-indigo-100",
      titleGradient: "from-blue-600 to-purple-600",
      features: [
        { icon: <Shield className="w-5 h-5" />, text: "Bank-grade security" },
        { icon: <Zap className="w-5 h-5" />, text: "Lightning-fast processing" },
        { icon: <CheckCircle className="w-5 h-5" />, text: "99.9% uptime guarantee" }
      ]
    },
    {
      id: 2,
      title: "Digital Payment",
      subtitle: "Revolution",
      description: "Transform your business operations with our comprehensive payment gateway solutions and advanced financial technology",
      image: "./100255326.png",
      gradient: "from-emerald-50 to-teal-100",
      titleGradient: "from-emerald-600 to-teal-600",
      features: [
        { icon: <CreditCard className="w-5 h-5" />, text: "Multiple payment methods" },
        { icon: <Globe className="w-5 h-5" />, text: "National reach" },
        { icon: <Activity className="w-5 h-5" />, text: "Real-time analytics" }
      ]
    },
    {
      id: 3,
      title: "Enterprise Video",
      subtitle: "Communications",
      description: "Connect your team and clients with our advanced video conferencing solutions and secure communication platform",
      image: "./video.png",
      gradient: "from-purple-50 to-pink-100",
      titleGradient: "from-purple-600 to-pink-600",
      features: [
        { icon: <Video className="w-5 h-5" />, text: "HD video quality" },
        { icon: <Lock className="w-5 h-5" />, text: "End-to-end encryption" },
        { icon: <Users className="w-5 h-5" />, text: "Video KYC" }
      ]
    },
    
{
  id: 4,
  title: "Electronic Document",
  subtitle: "Management",
  description: "Streamline your document workflows with our secure, scalable document management system and automated compliance solutions",
  image: "./security.jpg",
  gradient: "from-slate-50 to-gray-100",
  titleGradient: "from-slate-600 to-gray-600",
  features: [
    { icon: <FileText className="w-5 h-5" />, text: "Digital document storage" },
    { icon: <Search className="w-5 h-5" />, text: "Smart search & indexing" },
    { icon: <Shield className="w-5 h-5" />, text: "Compliance automation" }
  ]
}
  ];

  // Auto-play functionality
  useEffect(() => {
    let interval;
    if (isAutoPlay) {
      interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
      }, 5000); // Change slide every 5 seconds
    }
    return () => clearInterval(interval);
  }, [isAutoPlay, slides.length]);

  // Navigation functions
  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const currentSlideData = slides[currentSlide];

  return (
    <div className="relative">
      {/* Hero Section with Slider */}
      <section className={`bg-gradient-to-br ${currentSlideData.gradient} py-20 relative overflow-hidden transition-all duration-1000 ease-in-out`}>
        {/* Background Decoration */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-10 right-10 w-32 h-32 bg-white/10 rounded-full animate-pulse"></div>
          <div className="absolute bottom-10 left-10 w-24 h-24 bg-white/5 rounded-full animate-bounce" style={{animationDuration: '3s'}}></div>
          <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-white/10 rounded-full animate-ping"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[200px] lg:min-h-[300px]">
            {/* Left Content */}
            <div className="transform transition-all duration-1000 ease-out">
              <div key={currentSlide} className="animate-fadeInUp">
                <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                  {currentSlideData.title}
                  <br />
                  <span className={`text-transparent bg-clip-text bg-gradient-to-r ${currentSlideData.titleGradient}`}>
                    {currentSlideData.subtitle}
                  </span>
                </h1>
                
                <p className="text-xl text-gray-600 mb-8 leading-relaxed">
                  {currentSlideData.description}
                </p>
                
                {/* Feature highlights */}
                <div className="space-y-4 mb-8">
                  {currentSlideData.features.map((feature, index) => (
                    <div 
                      key={index}
                      className="flex items-center space-x-3 transform transition-all duration-700 animate-slideInLeft"
                      style={{ animationDelay: `${index * 200}ms` }}
                    >
                      <div className={`text-transparent bg-clip-text bg-gradient-to-r ${currentSlideData.titleGradient}`}>
                        {feature.icon}
                      </div>
                      <span className="text-gray-700 font-medium">{feature.text}</span>
                    </div>
                  ))}
                </div> 
              </div>
            </div>

            {/* Right Content - Image */}
            <div className="relative lg:w-full">
              <div className="transform transition-all duration-1000 ease-out">
                <div key={`image-${currentSlide}`} className="animate-fadeInRight">
                  <div className="relative group">
                    {/* Large Background Card Effect */}
                    <div className={`absolute inset-0 bg-gradient-to-r ${currentSlideData.titleGradient} rounded-3xl transform rotate-2 opacity-10 group-hover:rotate-3 transition-transform duration-500 scale-105`}></div>
                    
                    {/* Main Image Container - Much Larger */}
                    <div className="relative bg-white rounded-3xl p-4 shadow-2xl transform transition-all duration-500 group-hover:scale-102">
                      <img
                        src={currentSlideData.image}
                        alt={`${currentSlideData.title} ${currentSlideData.subtitle}`}
                        className="w-full h-96 lg:h-[350px] object-cover rounded-2xl shadow-lg"
                      />
                      
                      {/* Overlay Stats - Repositioned */}
                      <div className="absolute top-8 right-8 bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-xl border border-white/20">
                        <div className="flex items-center gap-3">
                          <div className={`w-4 h-4 bg-gradient-to-r ${currentSlideData.titleGradient} rounded-full animate-pulse`}></div>
                          <span className="text-base font-bold text-gray-800">Live</span>
                        </div>
                      </div>

                      {/* Bottom Stats - Larger and More Prominent */}
                      <div className="absolute bottom-8 left-8 right-8 bg-white/95 backdrop-blur-sm rounded-xl p-6 shadow-xl border border-white/20">
                        <div className="grid grid-cols-3 gap-6 text-center">
                          <div>
                            <div className={`text-3xl font-bold bg-gradient-to-r ${currentSlideData.titleGradient} bg-clip-text text-transparent`}>99.9%</div>
                            <div className="text-sm text-gray-600 font-medium">Uptime</div>
                          </div>
                          <div>
                            <div className={`text-3xl font-bold bg-gradient-to-r ${currentSlideData.titleGradient} bg-clip-text text-transparent`}>24/7</div>
                            <div className="text-sm text-gray-600 font-medium">Support</div>
                          </div>
                          <div>
                            <div className={`text-3xl font-bold bg-gradient-to-r ${currentSlideData.titleGradient} bg-clip-text text-transparent`}>1000+</div>
                            <div className="text-sm text-gray-600 font-medium">Clients</div>
                          </div>
                        </div>
                      </div>

                      {/* Additional Floating Elements */}
                      <div className="absolute top-1/2 -left-4 w-8 h-20 bg-white/80 rounded-full shadow-lg animate-pulse"></div>
                      <div className="absolute top-1/3 -right-6 w-12 h-12 bg-white/60 rounded-full shadow-lg animate-bounce" style={{animationDuration: '3s'}}></div>
                    </div>

                    {/* Decorative Elements */}
                    <div className={`absolute -top-8 -left-8 w-16 h-16 bg-gradient-to-r ${currentSlideData.titleGradient} rounded-full opacity-20 animate-spin`} style={{animationDuration: '8s'}}></div>
                    <div className={`absolute -bottom-6 -right-6 w-20 h-20 bg-gradient-to-r ${currentSlideData.titleGradient} rounded-full opacity-15 animate-pulse`}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Navigation Controls */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 flex items-center gap-6">
          {/* Previous Button */}
          <button
            onClick={prevSlide}
            onMouseEnter={() => setIsAutoPlay(false)}
            onMouseLeave={() => setIsAutoPlay(true)}
            className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-all duration-300 group"
          >
            <ChevronLeft className="w-6 h-6 text-gray-700 group-hover:text-gray-900" />
          </button>

          {/* Slide Indicators */}
          <div className="flex gap-3">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                onMouseEnter={() => setIsAutoPlay(false)}
                onMouseLeave={() => setIsAutoPlay(true)}
                className={`relative w-12 h-3 rounded-full transition-all duration-500 overflow-hidden ${
                  index === currentSlide 
                    ? 'bg-white shadow-lg' 
                    : 'bg-white/40 hover:bg-white/60'
                }`}
              >
                {index === currentSlide && (
                  <div className={`absolute inset-0 bg-gradient-to-r ${currentSlideData.titleGradient} rounded-full animate-slideProgress`}></div>
                )}
              </button>
            ))}
          </div>

          {/* Next Button */}
          <button
            onClick={nextSlide}
            onMouseEnter={() => setIsAutoPlay(false)}
            onMouseLeave={() => setIsAutoPlay(true)}
            className="w-12 h-12 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center hover:bg-white/30 transition-all duration-300 group"
          >
            <ChevronRight className="w-6 h-6 text-gray-700 group-hover:text-gray-900" />
          </button>
        </div>

        {/* Auto-play Indicator */}
        <div className="absolute top-8 right-8">
          <button
            onClick={() => setIsAutoPlay(!isAutoPlay)}
            className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
              isAutoPlay 
                ? 'bg-green-500/20 text-green-600' 
                : 'bg-red-500/20 text-red-600'
            }`}
          >
            {isAutoPlay ? <Play className="w-4 h-4" /> : <div className="w-3 h-3 bg-current rounded-sm" />}
          </button>
        </div>
      </section>

      {/* Custom Styles */}
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeInRight {
          from {
            opacity: 0;
            transform: translateX(30px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes slideInLeft {
          from {
            opacity: 0;
            transform: translateX(-20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes slideProgress {
          from {
            transform: scaleX(0);
            transform-origin: left;
          }
          to {
            transform: scaleX(1);
            transform-origin: left;
          }
        }

        .animate-fadeInUp {
          animation: fadeInUp 0.8s ease-out forwards;
        }

        .animate-fadeInRight {
          animation: fadeInRight 0.8s ease-out forwards;
        }

        .animate-slideInLeft {
          animation: slideInLeft 0.6s ease-out forwards;
        }

        .animate-slideProgress {
          animation: slideProgress 5s linear forwards;
        }
      `}</style>
    </div>
  );
};

export default HeroSlider;
